/**
 * The drawer's other views, as AgencyAnalytics': Conversation History (chats
 * and scheduled runs), Tools (Allow always / Ask / Block per tool) and the
 * agency's own Skills (only me or everyone).
 */

import { useEffect, useState, type ReactNode } from 'react';

import {
  assistantApi,
  type ConversationSummary,
  type Skill,
  type ToolPermission,
} from './assistant-api.ts';

const when = new Intl.DateTimeFormat('en-ZA', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

export function HistoryPanel(props: {
  organisationId: string;
  onOpen: (id: string) => void;
}): ReactNode {
  const [items, setItems] = useState<ConversationSummary[] | null>(null);
  const load = () =>
    assistantApi.conversations(props.organisationId).then(setItems, () => setItems([]));
  useEffect(() => void load(), [props.organisationId]);
  if (items === null) return <p className="as-empty">Loading…</p>;
  if (items.length === 0) return <p className="as-empty">No conversations yet.</p>;
  return (
    <ul className="as-list">
      {items.map((item) => (
        <li key={item.id}>
          <button type="button" className="as-list-main" onClick={() => props.onOpen(item.id)}>
            <strong>{item.title}</strong>
            <small>
              {item.source === 'SCHEDULE' ? 'Scheduled · ' : ''}
              {when.format(new Date(item.updated_at))}
            </small>
          </button>
          <button
            type="button"
            className="ex-link"
            aria-label={`Delete ${item.title}`}
            onClick={() => void assistantApi.remove(props.organisationId, item.id).then(load)}
          >
            Delete
          </button>
        </li>
      ))}
    </ul>
  );
}

const MODES: [ToolPermission['mode'], string][] = [
  ['ALLOW', 'Allow always'],
  ['ASK', 'Ask'],
  ['BLOCK', 'Block'],
];

export function ToolsPanel(props: { organisationId: string; canManage: boolean }): ReactNode {
  const [tools, setTools] = useState<ToolPermission[] | null>(null);
  useEffect(() => {
    assistantApi.tools(props.organisationId).then(setTools, () => setTools([]));
  }, [props.organisationId]);
  return (
    <div className="as-tools">
      <p className="kp-faint">
        What SynAssistant may do. Reading is allowed; anything that changes your account asks first.
      </p>
      <ul className="as-list">
        {(tools ?? []).map((tool) => (
          <li key={tool.tool}>
            <span className="as-list-main">
              <strong>{tool.tool.replace(/_/g, ' ')}</strong>
              <small>{tool.description}</small>
            </span>
            <select
              aria-label={`Permission for ${tool.tool}`}
              value={tool.mode}
              disabled={!props.canManage}
              onChange={(event) =>
                void assistantApi
                  .setTool(
                    props.organisationId,
                    tool.tool,
                    event.target.value as ToolPermission['mode'],
                  )
                  .then(setTools)
              }
            >
              {MODES.map(([mode, label]) => (
                <option key={mode} value={mode}>
                  {label}
                </option>
              ))}
            </select>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SkillsPanel(props: {
  organisationId: string;
  skills: Skill[];
  onChanged: (skills: Skill[]) => void;
}): ReactNode {
  const own = props.skills.filter((skill) => !skill.built_in);
  const [editing, setEditing] = useState<Skill | 'new' | null>(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    prompt: '',
    visibility: 'ONLY_ME' as 'ONLY_ME' | 'EVERYONE',
  });
  const [problem, setProblem] = useState<string | null>(null);
  const open = (skill: Skill | 'new') => {
    setEditing(skill);
    setProblem(null);
    setForm(
      skill === 'new'
        ? { name: '', description: '', prompt: '', visibility: 'ONLY_ME' }
        : {
            name: skill.name,
            description: skill.description,
            prompt: skill.prompt,
            visibility: skill.visibility ?? 'ONLY_ME',
          },
    );
  };
  const save = () =>
    assistantApi
      .saveSkill(props.organisationId, form, editing === 'new' ? undefined : editing?.id)
      .then((skills) => {
        props.onChanged(skills);
        setEditing(null);
      })
      .catch((error: unknown) =>
        setProblem(error instanceof Error ? error.message : 'The skill could not be saved.'),
      );
  if (editing)
    return (
      <form
        className="as-skill-form"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <label className="rs-field">
          Name
          <input
            value={form.name}
            maxLength={80}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            required
          />
        </label>
        <label className="rs-field">
          Description <small>(optional)</small>
          <input
            value={form.description}
            maxLength={300}
            onChange={(event) => setForm({ ...form, description: event.target.value })}
          />
        </label>
        <label className="rs-field">
          Instructions
          <textarea
            rows={6}
            maxLength={4000}
            value={form.prompt}
            onChange={(event) => setForm({ ...form, prompt: event.target.value })}
            required
          />
        </label>
        <label className="rs-field">
          Shared with
          <select
            value={form.visibility}
            onChange={(event) =>
              setForm({ ...form, visibility: event.target.value as 'ONLY_ME' | 'EVERYONE' })
            }
          >
            <option value="ONLY_ME">Only me</option>
            <option value="EVERYONE">Everyone in the agency</option>
          </select>
        </label>
        {problem && <p className="kp-problem">{problem}</p>}
        <div className="kp-dialog-actions">
          <button type="button" className="btn secondary" onClick={() => setEditing(null)}>
            Cancel
          </button>
          <button type="submit" className="btn">
            Save skill
          </button>
        </div>
      </form>
    );
  return (
    <div>
      <button type="button" className="btn secondary" onClick={() => open('new')}>
        + New skill
      </button>
      <ul className="as-list">
        {own.map((skill) => (
          <li key={skill.id}>
            <span className="as-list-main">
              <strong>{skill.name}</strong>
              <small>{skill.visibility === 'EVERYONE' ? 'Everyone' : 'Only me'}</small>
            </span>
            {skill.is_mine && (
              <>
                <button type="button" className="ex-link" onClick={() => open(skill)}>
                  Edit
                </button>
                <button
                  type="button"
                  className="ex-link"
                  onClick={() =>
                    void assistantApi
                      .removeSkill(props.organisationId, skill.id)
                      .then(props.onChanged)
                  }
                >
                  Delete
                </button>
              </>
            )}
          </li>
        ))}
      </ul>
      {own.length === 0 && (
        <p className="as-empty">
          Write your own skills: instructions you use often, one click away in the / menu.
        </p>
      )}
    </div>
  );
}
